import { useMutation, useQueryClient } from "@tanstack/react-query";

import { bffAxios } from "@av/config/api-config/axios-config";
import {
  enableCompanyDocumentInAva,
  getPdfContentType,
  rollbackCreatedCompanyDocument,
  uploadCompanyDocumentFile,
} from "../company-document-api";
import { setCompanyDocumentInCache } from "../company-document-query-cache";

import type {
  ApiDocumentResponse,
  CompanyDocumentMutationValues,
  CompanyDocumentUploadView,
} from "../types";

export function useCreateCompanyDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, ...values }: CompanyDocumentMutationValues) => {
      const { data } = await bffAxios.post<ApiDocumentResponse<CompanyDocumentUploadView>>(
        "/company-documents",
        {
          name: values.name,
          description: values.description,
          filename: file.name,
          contentType: getPdfContentType(file),
          fileSizeBytes: file.size,
          useInAva: false,
        }
      );

      const createdDocument = data.data.document;

      try {
        await uploadCompanyDocumentFile(file, data.data.upload);

        if (values.useInAva) {
          const updatedDocument = await enableCompanyDocumentInAva(
            createdDocument.companyDocumentId
          );

          return updatedDocument;
        }

        return createdDocument;
      } catch (error) {
        await rollbackCreatedCompanyDocument(createdDocument.companyDocumentId);

        throw error;
      }
    },
    onSuccess: (document) => setCompanyDocumentInCache(queryClient, document),
  });
}
